/**
 * Custom studio — the format presets an owner starts a new creative from, and the small typed
 * model behind the editor. Dimensions only (no colours here); the palette lives in tokens.
 *
 * The draft is a set of freely-placed elements over a background: a headline, a subheadline, a
 * call-to-action pill, and the logo. Each movable element carries its own position (the CENTRE of
 * the element, as a 0..1 fraction of the canvas) so it can be dragged directly on the canvas, plus
 * its own styling. Earlier drafts used a 3x3 alignment grid instead of free positions; `normalizeDraft`
 * migrates those forward so saved creatives keep opening.
 */

export type FormatGroup = 'Social' | 'Stories & Reels' | 'Display ads';

export interface CreativeFormat {
  id: string;
  name: string;
  group: FormatGroup;
  /** Native pixel size (drives the aspect ratio + the export resolution). */
  w: number;
  h: number;
}

export const CREATIVE_FORMATS: readonly CreativeFormat[] = [
  { id: 'ig-post', name: 'Instagram Post', group: 'Social', w: 1080, h: 1080 },
  { id: 'ig-landscape', name: 'Instagram Landscape', group: 'Social', w: 1080, h: 566 },
  { id: 'fb-post', name: 'Facebook Post', group: 'Social', w: 1200, h: 630 },
  { id: 'x-post', name: 'X / Twitter Post', group: 'Social', w: 1600, h: 900 },
  { id: 'ig-story', name: 'Instagram Story', group: 'Stories & Reels', w: 1080, h: 1920 },
  { id: 'tiktok', name: 'TikTok / Reel', group: 'Stories & Reels', w: 1080, h: 1920 },
  { id: 'billboard', name: 'Billboard', group: 'Display ads', w: 970, h: 250 },
  { id: 'leaderboard', name: 'Leaderboard', group: 'Display ads', w: 728, h: 90 },
  { id: 'medium-rectangle', name: 'Medium Rectangle', group: 'Display ads', w: 300, h: 250 },
  { id: 'half-page', name: 'Half Page', group: 'Display ads', w: 300, h: 600 },
  { id: 'wide-skyscraper', name: 'Wide Skyscraper', group: 'Display ads', w: 160, h: 600 },
];

export const FORMAT_GROUPS: readonly FormatGroup[] = ['Social', 'Stories & Reels', 'Display ads'];

export type TextSize = 'sm' | 'md' | 'lg';
/** Multi-line justification within a text element (does not move the element). */
export type TextAlign = 'left' | 'center' | 'right';

/** A movable element's centre, as a 0..1 fraction of the canvas (x = left→right, y = top→bottom). */
export interface Vec {
  x: number;
  y: number;
}

/** Which element is being edited / dragged / shown in the contextual panel. */
export type ElementId = 'headline' | 'subheadline' | 'cta' | 'logo';
export type Selection = ElementId | 'background' | { extra: string } | null;
/** The elements that can be selected + edited on the canvas (excludes the background). */
export const ELEMENT_IDS: readonly ElementId[] = ['headline', 'subheadline', 'cta', 'logo'];

export interface TextLayer {
  text: string;
  color: string;
  size: TextSize;
  align: TextAlign;
  /** Centre of the text block. */
  pos: Vec;
  /** Box width as a fraction of the canvas width (left/right handles) — controls wrapping. */
  width: number;
  /** Font multiplier on `size`. A CORNER (diagonal) resize scales `width` + `scale` together, so it
   *  grows uniformly WITHOUT reflowing; a side handle changes `width` alone (reflows). */
  scale: number;
  /** Rotation in degrees (rotate handle); default 0. */
  rot: number;
}

export interface CtaLayer {
  text: string;
  color: string;
  show: boolean;
  /** Centre of the pill. */
  pos: Vec;
  /** Pill box as fractions of the canvas (how much it fills) — width/height handles. */
  width: number;
  height: number;
  /** Font multiplier; a corner resize scales width + height + scale together. */
  scale: number;
  /** Rotation in degrees; default 0. */
  rot: number;
}

export interface LogoLayer {
  show: boolean;
  /** Centre of the logo. */
  pos: Vec;
  /** Logo height as a fraction of the canvas's smaller side (also the resize handle). */
  scale: number;
  /** Rotation in degrees; default 0. */
  rot: number;
}

export type BgKind = 'gradient' | 'color' | 'image';
export interface Background {
  kind: BgKind;
  /** For `color`: a hex. For `gradient`: a gradient id (see CREATIVE_GRADIENT_STOPS). */
  value: string;
  /** For `image`: the generated/uploaded artwork URL. */
  imageUrl?: string;
  /** Darken an image background so overlaid text stays legible (0–0.6). */
  scrim: number;
  /** Pan offset for an image background, each axis −0.5..0.5 of the frame (0 = centred). */
  offset: Vec;
}

/** An extra, freely-placed image element added from the "+" menu (a logo, sticker or decoration). */
export type ExtraKind = 'logo' | 'sticker';
export interface ExtraElement {
  id: string;
  kind: ExtraKind;
  /** Image URL / data-URI. */
  src: string;
  /** Centre of the element. */
  pos: Vec;
  /** Height as a fraction of the canvas's smaller side. */
  scale: number;
  /** Rotation in degrees. */
  rot: number;
}

export interface CreativeDraft {
  headline: TextLayer;
  subheadline: TextLayer;
  cta: CtaLayer;
  logo: LogoLayer;
  background: Background;
  /** Extra placed elements (logos / stickers), newest last (draw order). */
  extras: ExtraElement[];
}

/** Default logo height (fraction of the smaller side). */
export const DEFAULT_LOGO_SCALE = 0.12;
/** Default box widths/heights (fractions of the canvas). */
export const DEFAULT_HEADLINE_W = 0.84;
export const DEFAULT_SUB_W = 0.72;
export const DEFAULT_CTA_W = 0.36;
export const DEFAULT_CTA_H = 0.06;

/** Build a CSS `linear-gradient(...)` from a structured gradient (tokens.CREATIVE_GRADIENT_STOPS). */
export const gradientCss = (g: { angle: number; stops: readonly string[] }): string =>
  `linear-gradient(${g.angle}deg, ${g.stops.join(', ')})`;

/** Relative type scale for the three text sizes, as a fraction of the canvas's smaller side. */
export const TEXT_SCALE: Record<TextSize, number> = { sm: 0.045, md: 0.075, lg: 0.12 };

const clamp01 = (n: number): number => (Number.isFinite(n) ? Math.min(1, Math.max(0, n)) : 0.5);
const isVec = (v: unknown): v is Vec =>
  typeof v === 'object' && v !== null && typeof (v as Vec).x === 'number' && typeof (v as Vec).y === 'number';

/** Legacy 3x3 alignment → an element centre, so old saved drafts keep opening. */
const legacyAnchor = (h: unknown, v: unknown): Vec => {
  const x = h === 'left' ? 0.28 : h === 'right' ? 0.72 : 0.5;
  const y = v === 'top' ? 0.18 : v === 'bottom' ? 0.7 : 0.44;
  return { x, y };
};
const LEGACY_LOGO_POS: Record<string, Vec> = {
  'top-left': { x: 0.13, y: 0.1 },
  'top-right': { x: 0.87, y: 0.1 },
  'bottom-left': { x: 0.13, y: 0.9 },
  'bottom-right': { x: 0.87, y: 0.9 },
};

/**
 * Coerce any stored/loaded draft to the current free-position shape. New drafts pass through
 * (with defensive fills); legacy drafts (3x3 `layout` + a `logo` position string) are migrated:
 * the text stack is spread out from its old anchor and the logo keeps its corner.
 */
export const normalizeDraft = (raw: unknown): CreativeDraft => {
  const r = (raw ?? {}) as Record<string, unknown>;
  const bgRaw = (r.background ?? {}) as Record<string, unknown>;
  const background: Background = {
    kind: bgRaw.kind === 'color' || bgRaw.kind === 'image' ? (bgRaw.kind as BgKind) : 'gradient',
    value: typeof bgRaw.value === 'string' ? bgRaw.value : 'aurora',
    imageUrl: typeof bgRaw.imageUrl === 'string' ? bgRaw.imageUrl : undefined,
    scrim: typeof bgRaw.scrim === 'number' ? bgRaw.scrim : 0.12,
    offset: isVec(bgRaw.offset) ? bgRaw.offset : { x: 0, y: 0 },
  };
  const extras: ExtraElement[] = Array.isArray(r.extras)
    ? r.extras
        .map((e) => e as Record<string, unknown>)
        .filter((e) => typeof e.id === 'string' && typeof e.src === 'string')
        .map((e) => ({
          id: String(e.id),
          kind: e.kind === 'logo' ? 'logo' : 'sticker',
          src: String(e.src),
          pos: isVec(e.pos) ? { x: clamp01(e.pos.x), y: clamp01(e.pos.y) } : { x: 0.5, y: 0.5 },
          scale: typeof e.scale === 'number' ? e.scale : 0.2,
          rot: typeof e.rot === 'number' ? e.rot : 0,
        }))
    : [];

  const head = (r.headline ?? {}) as Record<string, unknown>;
  const sub = (r.subheadline ?? {}) as Record<string, unknown>;
  const cta = (r.cta ?? {}) as Record<string, unknown>;
  const logo = r.logo;

  // New shape already carries per-element positions.
  if (isVec(head.pos) && typeof logo === 'object' && logo !== null) {
    const l = logo as Record<string, unknown>;
    return {
      headline: text(head, { x: 0.5, y: 0.6 }, DEFAULT_HEADLINE_W),
      subheadline: text(sub, { x: 0.5, y: 0.74 }, DEFAULT_SUB_W),
      cta: {
        text: typeof cta.text === 'string' ? cta.text : '',
        color: typeof cta.color === 'string' ? cta.color : '#ffffff',
        show: cta.show !== false,
        pos: isVec(cta.pos) ? cta.pos : { x: 0.5, y: 0.86 },
        width: typeof cta.width === 'number' ? cta.width : DEFAULT_CTA_W,
        height: typeof cta.height === 'number' ? cta.height : DEFAULT_CTA_H,
        scale: typeof cta.scale === 'number' ? cta.scale : 1,
        rot: typeof cta.rot === 'number' ? cta.rot : 0,
      },
      logo: {
        show: l.show !== false,
        pos: isVec(l.pos) ? l.pos : { x: 0.5, y: 0.1 },
        scale: typeof l.scale === 'number' ? l.scale : DEFAULT_LOGO_SCALE,
        rot: typeof l.rot === 'number' ? l.rot : 0,
      },
      background,
      extras,
    };
  }

  // Legacy shape: derive positions from the old 3x3 layout + logo corner.
  const layout = (r.layout ?? {}) as Record<string, unknown>;
  const anchor = legacyAnchor(layout.h, layout.v);
  const align: TextAlign = layout.h === 'center' ? 'center' : layout.h === 'right' ? 'right' : 'left';
  const legacyLogo = typeof logo === 'string' ? logo : 'none';
  return {
    headline: text({ ...head, align }, { x: anchor.x, y: anchor.y }, DEFAULT_HEADLINE_W),
    subheadline: text({ ...sub, align }, { x: anchor.x, y: clamp01(anchor.y + 0.12) }, DEFAULT_SUB_W),
    cta: {
      text: typeof cta.text === 'string' ? cta.text : '',
      color: typeof cta.color === 'string' ? cta.color : '#ffffff',
      show: cta.show !== false,
      pos: { x: anchor.x, y: clamp01(anchor.y + 0.26) },
      width: typeof cta.width === 'number' ? cta.width : DEFAULT_CTA_W,
      height: typeof cta.height === 'number' ? cta.height : DEFAULT_CTA_H,
      scale: typeof cta.scale === 'number' ? cta.scale : 1,
      rot: typeof cta.rot === 'number' ? cta.rot : 0,
    },
    logo: {
      show: legacyLogo !== 'none',
      pos: LEGACY_LOGO_POS[legacyLogo] ?? { x: 0.5, y: 0.1 },
      scale: DEFAULT_LOGO_SCALE,
      rot: 0,
    },
    background,
    extras,
  };
};

/** A new extra element, centred, at a sensible default size. */
export const makeExtra = (kind: ExtraKind, src: string, id: string): ExtraElement => ({
  id,
  kind,
  src,
  pos: { x: 0.5, y: 0.5 },
  scale: kind === 'sticker' ? 0.18 : DEFAULT_LOGO_SCALE,
  rot: 0,
});

/** Build a validated TextLayer from a raw object, defaulting position, box width + style. */
const text = (raw: Record<string, unknown>, fallbackPos: Vec, fallbackW: number): TextLayer => ({
  text: typeof raw.text === 'string' ? raw.text : '',
  color: typeof raw.color === 'string' ? raw.color : '#ffffff',
  size: raw.size === 'sm' || raw.size === 'md' || raw.size === 'lg' ? raw.size : 'md',
  align: raw.align === 'center' || raw.align === 'right' ? raw.align : 'left',
  pos: isVec(raw.pos) ? { x: clamp01(raw.pos.x), y: clamp01(raw.pos.y) } : fallbackPos,
  width: typeof raw.width === 'number' ? raw.width : fallbackW,
  scale: typeof raw.scale === 'number' ? raw.scale : 1,
  rot: typeof raw.rot === 'number' ? raw.rot : 0,
});
